import { formatCount, tt } from '@agos/shared'

/**
 * `/notifications` — what the system has noticed, and what it has not been told about.
 *
 * A different page from `/settings-notifications`, which decides how *customers* are told things. This one is
 * the staff inbox: low stock, an invoice past due, a purchase order the system drafted. They share a
 * controller and nothing else, so they stay two pages.
 *
 * **FB-146.** The unread count is computed from the page that happens to be loaded:
 *
 * ```tsx
 * const unreadCount = useMemo(() => list.filter(n => !n.readAt).length, [list]);
 * …
 * description={`${unreadCount} neprečítaných · zobrazené ${list.length} z ${total}`}
 * ```
 *
 * `total` is the server's figure; `unreadCount` is not. On the restored data the inbox holds **164** rows of
 * which **12** are unread, and the first page is 25 — so the header reads correctly only while every unread
 * row happens to be in the loaded window. `GET /notifications/unread-count` exists and answers 12; the page
 * derives a number the server already knows.
 *
 * **FB-147.** `<T>{n.title}</T>` puts **data** through the translation memory. A title is generated —
 * *Nízka zásoba: Múka (2 kg)* — so every notification becomes a dictionary key, and the missing-translation
 * endpoint is called for each one. Titles are already Slovak because the backend wrote them in Slovak; the
 * body beside them is rendered as text, which is the correct treatment for both.
 */

export type Severity = 'info' | 'warning' | 'error'

export interface Notification {
  id: number
  kind: string
  severity: Severity
  title: string
  body: string | null
  refTable: string | null
  refId: number | null
  readAt: string | null
  createdAt: string
}

export interface InboxPage {
  notifications: Notification[]
  /** The server's count of everything matching the filter, not of what was returned. */
  total: number
}

export const PAGE_SIZE = 25

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function pageOf(payload: unknown): InboxPage {
  const row = payload as { notifications?: unknown; total?: unknown } | undefined
  const list = Array.isArray(row?.notifications) ? row.notifications : []
  return {
    notifications: list.map((entry) => {
      const one = entry as Record<string, unknown>
      const severity = one.severity
      return {
        id: typeof one.id === 'number' ? one.id : 0,
        kind: text(one.kind) ?? '',
        severity: severity === 'warning' || severity === 'error' ? severity : 'info',
        title: text(one.title) ?? '',
        body: text(one.body),
        refTable: text(one.refTable),
        refId: typeof one.refId === 'number' ? one.refId : null,
        readAt: text(one.readAt),
        createdAt: text(one.createdAt) ?? '',
      }
    }),
    total: typeof row?.total === 'number' ? row.total : list.length,
  }
}

/** The three kinds the restored data holds, and the label each gets. */
export function kindLabel(kind: string): string {
  if (kind === 'low_stock') return tt('Sklad')
  if (kind === 'invoice_overdue') return tt('Po splatnosti')
  if (kind === 'invoice_due_soon') return tt('Splatnosť čoskoro')
  if (kind === 'reorder_draft') return tt('Návrh objednávky')
  if (kind === 'recurring_invoice') return tt('Opakovaná faktúra')
  // §9.348, the owner's C4/C6. These five arrive from `DailyCheck`, which had no clock until
  // BACKEND.md §10A.136 gave it one - so until this session they were kinds nothing produced, and
  // the fall-through below would have printed `vehicle_document_due` at a person.
  if (kind === 'vehicle_document_expiring') return tt('Vozový park')
  if (kind === 'vehicle_document_due') return tt('Vozový park - do 14 dní')

  if (kind === 'vehicle_document_expired') return tt('Vozový park - prepadnuté')
  if (kind === 'outgoing_invoice_overdue') return tt('Vydaná faktúra po splatnosti')
  if (kind === 'outgoing_invoice_draft_stale') return tt('Nevystavená faktúra')
  // A kind nobody has labelled is shown as its key rather than hidden — the key is what a report would
  // be filed against.
  return kind
}

export function isLabelledKind(kind: string): boolean {
  return kindLabel(kind) !== kind
}

/** Where a notification points, when it points anywhere. */
export function linkFor(notification: Notification): string | null {
  if (notification.refTable === null || notification.refId === null) return null
  if (notification.refTable === 'stock_items') return '/stock'
  if (notification.refTable === 'invoices') return `/invoices?openId=${notification.refId}`
  if (notification.refTable === 'reorder_drafts') return '/reorder-drafts'
  // *"integrate it in our system"* - a notice that a van's STK has lapsed pointed nowhere, which is
  // the difference between being told and being able to do something about it.

  if (notification.refTable === 'vehicles') return '/vehicle-fleet'
  if (notification.refTable === 'outgoing_invoices') return '/outgoing-invoices'
  return null
}

/**
 * FB-146: the header's numbers, each from where it actually comes from.
 *
 * `unread` is the server's count for the whole inbox. `shown` and `total` describe the window. Legacy mixes
 * a windowed figure with a whole-inbox one in the same sentence.
 */
export interface InboxCounts {
  shown: number
  total: number
  unread: number | null
}

export function countsDescription(counts: InboxCounts): string {
  const window = `${tt('zobrazených')} ${formatCount(counts.shown)} ${tt('z')} ${formatCount(counts.total)}`
  if (counts.unread === null) {
    // The count endpoint failed; a number derived from one page would look like the same fact.
    return `${window} · ${tt('počet neprečítaných sa nepodarilo zistiť')}`
  }
  return `${formatCount(counts.unread)} ${tt('neprečítaných v celej schránke')} · ${window}`
}

/** Whether there is another page to ask for. */
export function hasMore(counts: Pick<InboxCounts, 'shown' | 'total'>): boolean {
  return counts.shown < counts.total
}

export function nextPageSize(counts: Pick<InboxCounts, 'shown' | 'total'>): number {
  return Math.max(0, Math.min(PAGE_SIZE, counts.total - counts.shown))
}

// `severityOrder` was here and is deleted — §9.35, 2026-08-06. A sort key for a list that must not be
// sorted here.
//
// The inbox is **paged** — `hasMore` and `nextPageSize` above are both wired — so the order is the
// server's and the client holds one page of it. Sorting that page by severity would float the errors
// *within the page*, putting page two's errors below page one's notices: a list that looks ordered
// and is not, which is worse than the chronological one it replaced. Nothing called it, and nothing
// should have.

/**
 * What the *daily check* does, said before it is pressed.
 *
 * `DailyCheck.run()` drafts purchase orders and **e-mails every administrator** — its own controller comment
 * says the endpoint is `main_admin` for exactly that reason. Legacy's button fires on the first click.
 */
export function dailyCheckWarning(): string {
  return tt(
    'Kontrola prejde sklad a faktúry, vytvorí návrhy objednávok a pošle e-mail všetkým administrátorom. Beží aj sama, raz denne.',
  )
}

/**
 * What needs doing, from the unread ones - §9.348, the owner's C6.
 *
 * **Unread only, deliberately.** A page of notices somebody has already read needs no attention
 * line; counting all of them would have the owner's four-month-old stock report demanding action for
 * ever, which is the state they described as *"shitty"*.
 *
 * **Errors before warnings, and the kinds named.** *2 things need attention* is a number; *Vozový
 * park, Vydané faktúry* is where to go. The kinds are named in severity order and capped at three -
 * a summary that lists everything is the list it was meant to summarise.
 */
export function inboxSummary(notifications: readonly Notification[]): string {
  const unread = notifications.filter((notification) => notification.readAt === null)
  if (unread.length === 0) return ''

  const errors = unread.filter((notification) => notification.severity === 'error')
  const needing = errors.length > 0 ? errors : unread
  const kinds: string[] = []
  for (const notification of needing) {
    const label = kindLabel(notification.kind)
    if (!kinds.includes(label)) kinds.push(label)
  }
  const named = kinds.slice(0, 3).join(', ')
  const rest = kinds.length > 3 ? ` +${formatCount(kinds.length - 3)}` : ''
  const what = errors.length > 0 ? tt('vyžaduje pozornosť') : tt('neprečítaných')
  return `${formatCount(needing.length)} ${what}: ${named}${rest}`
}
